import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../NotificationBell';
import ClientShell from './ClientShell';
import {
    LayoutDashboard,
    Briefcase,
    Settings,
    LogOut,
    Menu,
    X,
    User,
    Shield,
    Wrench,
    Eye,
    CheckCircle
} from 'lucide-react';

export default function Layout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userProfile, logout } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);

    const isAdmin = userProfile?.role === 'admin';
    const isContractor = userProfile?.role === 'contractor';
    const isClient = !isAdmin && !isContractor;

    // Navigation items based on user role
    const getNavigation = () => {
        if (isAdmin) {
            return [
                { name: 'Dashboard', href: '/admin', icon: LayoutDashboard }
            ];
        }

        if (isContractor) {
            return [
                { name: 'Dashboard', href: '/contractor/dashboard', icon: LayoutDashboard },
                { name: 'Available Jobs', href: '/contractor/available-jobs', icon: Eye },
                { name: 'My Jobs', href: '/contractor/my-jobs', icon: Briefcase },
            ];
        }

        // Client navigation
        return [
            { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
            { name: 'My Jobs', href: '/jobs', icon: Briefcase },
        ];
    };

    const navigation = [
        ...getNavigation(),
        { name: 'Settings', href: isContractor ? '/contractor/profile' : '/settings', icon: Settings }
    ];

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    // Get user icon and badge based on role
    const getUserIcon = () => {
        if (isAdmin) return Shield;
        if (isContractor) return Wrench;
        return User;
    };

    const getUserBadgeColor = () => {
        if (isAdmin) return 'bg-red-600';
        if (isContractor) return 'bg-purple-600';
        return 'bg-blue-600';
    };

    const getUserRoleLabel = () => {
        if (isAdmin) return 'Administrator';
        if (isContractor) return 'Contractor';
        return userProfile?.companyName || 'Client';
    };

    const UserIcon = getUserIcon();

    // Client-only redesign: admin and contractor layouts retain their proven routes.
    if (isClient) return <ClientShell currentUser={currentUser} userProfile={userProfile} logout={logout}>{children}</ClientShell>;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top Navigation */}
            <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <div className="flex items-center space-x-8">
                            <Link 
                                to={
                                    isAdmin ? "/admin" : 
                                    isContractor ? "/contractor/dashboard" : 
                                    "/dashboard"
                                } 
                                className="flex items-center space-x-3"
                            >
                                <div className="w-12 h-12 rounded-full flex items-center justify-center p-2.5 bg-white border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                    <img
                                        src="/logo-icon.png"
                                        alt="Chuko Logic"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <span className="text-xl font-bold text-gray-900 hidden sm:block">
                                    Chuko Logic
                                </span>
                            </Link>

                            {/* Desktop Navigation */}
                            <div className="hidden md:flex items-center space-x-1">
                                {navigation.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.href);

                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                                active
                                                    ? 'bg-blue-50 text-blue-600'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            <Icon className="w-5 h-5" />
                                            <span className="font-medium">{item.name}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right Side */}
                        <div className="flex items-center space-x-4">
                            {/* Book Service Button - Clients Only */}
                            {isClient && (
                                <Link
                                    to="/book-service"
                                    className="hidden sm:inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    Book Service
                                </Link>
                            )}

                            {/* Notifications */}
                            <div className="relative">
                                <NotificationBell />
                            </div>

                            {/* Profile Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getUserBadgeColor()}`}>
                                        <UserIcon className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="hidden sm:block text-left">
                                        <p className="text-sm font-medium text-gray-900">
                                            {userProfile?.name || userProfile?.contactName || 'User'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {getUserRoleLabel()}
                                        </p>
                                    </div>
                                </button>

                                {/* Profile Dropdown */}
                                <AnimatePresence>
                                    {profileMenuOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2"
                                        >
                                            <div className="px-4 py-3 border-b border-gray-200">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {userProfile?.name || userProfile?.contactName}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {currentUser?.email}
                                                </p>
                                                <p className={`text-xs font-semibold mt-1 ${
                                                    isAdmin ? 'text-red-600' : 
                                                    isContractor ? 'text-purple-600' : 
                                                    'text-blue-600'
                                                }`}>
                                                    {isAdmin && 'Administrator Access'}
                                                    {isContractor && 'Contractor Access'}
                                                    {isClient && 'Client Access'}
                                                </p>
                                            </div>

                                            <Link
                                                to={isContractor ? '/contractor/profile' : '/settings'}
                                                onClick={() => setProfileMenuOpen(false)}
                                                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                            >
                                                <Settings className="w-4 h-4" />
                                                <span>{isContractor ? 'Profile' : 'Settings'}</span>
                                            </Link>

                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                <span>Sign out</span>
                                            </button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                {mobileMenuOpen ? (
                                    <X className="w-6 h-6" />
                                ) : (
                                    <Menu className="w-6 h-6" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="md:hidden border-t border-gray-200"
                        >
                            <div className="px-4 py-4 space-y-2">
                                {navigation.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.href);

                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                                                active
                                                    ? 'bg-blue-50 text-blue-600'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            <Icon className="w-5 h-5" />
                                            <span className="font-medium">{item.name}</span>
                                        </Link>
                                    );
                                })}

                                {isClient && (
                                    <Link
                                        to="/book-service"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Book Service
                                    </Link>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>

            {/* Main Content */}
            <main>
                {children}
            </main>

            {/* Click outside to close menus */}
            {profileMenuOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setProfileMenuOpen(false)}
                />
            )}
        </div>
    );
}
